/**
 * @copyright
 * (c) 2026 netTrek GmbH & Co. KG
 *
 * Transforms a decoded CAWG identity assertion (`cawg.identity`) into the shape
 * c2pa-rs reports, keyed on the `signer_payload.sig_type`:
 *
 *  - `cawg.x509.cose` — the embedded identity COSE_Sign1 (`signature`) is parsed
 *    into a `signature_info`; the raw signature + padding are dropped and the
 *    `signer_payload` is kept.
 *  - `cawg.identity_claims_aggregation` — the embedded `signature` is a COSE_Sign1
 *    wrapping a W3C Verifiable Credential (`application/vc-ld+cose`, JSON payload).
 *    c2pa-rs reports the credential's `IcaCredentialSummary`: the VC's top-level
 *    fields plus `verifiedIdentities` hoisted out of `credentialSubject` (which is
 *    otherwise dropped). Mirrors `c2pa::identity::claim_aggregation`.
 *
 * See {@link https://cawg.io/identity/}.
 */

import { decodeSign1, coseAlg, x5chain } from '../cose/decodeSign1'
import { algSpec } from '../cose/alg'
import { parseCertificate } from '../x509/parseCert'
import { toJsonValue } from '../util/toJsonValue'

/** VerifiedIdentity fields c2pa-rs models, in declaration order (others dropped). */
function pickVerifiedIdentity (vi: unknown): Record<string, unknown> | null {
    if (!vi || typeof vi !== 'object') return null
    const v = vi as Record<string, unknown>
    const out: Record<string, unknown> = {}
    if (v.type !== undefined) out.type = v.type
    if (v.name !== undefined) out.name = v.name
    if (v.username !== undefined) out.username = v.username
    if (v.address !== undefined) out.address = v.address
    if (v.uri !== undefined) out.uri = v.uri
    if (v.verifiedAt !== undefined) out.verifiedAt = v.verifiedAt
    if (v.provider && typeof v.provider === 'object') {
        const p = v.provider as Record<string, unknown>
        const prov: Record<string, unknown> = {}
        if (p.id !== undefined) prov.id = p.id
        if (p.name !== undefined) prov.name = p.name
        out.provider = prov
    }
    return out
}

/**
 * Builds the `IcaCredentialSummary` from a parsed W3C Verifiable Credential: the
 * recognized CredentialV2 fields, `verifiedIdentities` lifted from the first
 * `credentialSubject`, and the remaining top-level VC properties (e.g.
 * `credentialSchema`) passed through. `credentialSubject` itself is dropped.
 */
function icaCredentialSummary (vc: Record<string, unknown>): Record<string, unknown> {
    const out: Record<string, unknown> = {}
    if (vc['@context'] !== undefined) out['@context'] = vc['@context']
    if (vc.id !== undefined) out.id = vc.id
    if (vc.type !== undefined) out.type = vc.type
    if (vc.issuer !== undefined) out.issuer = vc.issuer
    if (vc.validFrom !== undefined) out.validFrom = vc.validFrom
    if (vc.validUntil !== undefined) out.validUntil = vc.validUntil

    // credentialSubject may be an object or an array (serde one_or_many → first).
    const cs = vc.credentialSubject
    const subject = (Array.isArray(cs) ? cs[0] : cs) as Record<string, unknown> | undefined
    const vis = subject?.verifiedIdentities
    if (Array.isArray(vis)) out.verifiedIdentities = vis.map(pickVerifiedIdentity).filter(Boolean)

    // Remaining top-level VC properties (extra_properties), excluding the ones
    // already mapped above and credentialSubject (which is collapsed).
    const handled = new Set(['@context', 'id', 'type', 'issuer', 'validFrom', 'validUntil', 'credentialSubject'])
    for (const [k, v] of Object.entries(vc)) {
        if (!handled.has(k)) out[k] = v
    }
    return out
}

/**
 * Returns the transformed assertion data, or `null` if the value isn't a
 * recognizable CAWG identity assertion (caller should fall back to the raw data).
 */
export function transformCawgIdentity (decoded: unknown, withholdSignatureInfo = false): Record<string, unknown> | null {
    if (!decoded || typeof decoded !== 'object') return null
    const d = decoded as Record<string, unknown>
    const signerPayload = d.signer_payload
    if (!signerPayload || typeof signerPayload !== 'object') return null
    const sigType = (signerPayload as Record<string, unknown>).sig_type

    // ICA verifiable-credential form: report the credential summary; the embedded
    // COSE_Sign1 (`signature`) wraps the VC as a JSON payload.
    if (sigType === 'cawg.identity_claims_aggregation') {
        const sig = d.signature
        if (sig instanceof Uint8Array) {
            try {
                const sign1 = decodeSign1(sig)
                if (sign1.payload) {
                    const vc = JSON.parse(new TextDecoder().decode(sign1.payload)) as Record<string, unknown>
                    return icaCredentialSummary(vc)
                }
            } catch { /* fall through to raw */ }
        }
        return null
    }

    // x509.cose form: keep signer_payload and parse the embedded identity COSE's
    // signing cert into signature_info.
    const out: Record<string, unknown> = {}
    out.signer_payload = toJsonValue(signerPayload)
    const sig = d.signature
    if (sig instanceof Uint8Array && !withholdSignatureInfo) {
        try {
            const sign1 = decodeSign1(sig)
            const chain = x5chain(sign1)
            if (chain.length > 0) {
                const cert = parseCertificate(chain[0])
                const alg = coseAlg(sign1)
                out.signature_info = {
                    alg: alg !== undefined ? algSpec(alg)?.signingAlg ?? null : null,
                    issuer: cert.subject.O ?? null,
                    cert_serial_number: cert.serialNumber,
                    revocation_status: true
                }
            }
        } catch { /* leave signature_info absent on parse failure */ }
    }
    return out
}
